<template>
  <div class="dashboard-container modern-scrollbar">
    <div class="dashboard-grid">
      <!-- Left Column -->
      <div class="left-column">
        <!-- Prices Card -->
        <div class="dashboard-card market-service-card">
          <div class="card-header">
            <h3 class="card-title">{{ t("App.sidebarMenu.prices") }}</h3>
            <div style="display: flex; align-items: center; gap: 6px;">
              <NTooltip v-if="attention.prices" trigger="hover" placement="top">
                <template #trigger>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#f0a020" stroke-width="2"
                       style="width: 26px; height: 26px; flex-shrink: 0; cursor: default; outline: none;">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <circle cx="12" cy="17" r="0.5" fill="#f0a020"/>
                  </svg>
                </template>
                {{ attentionReason.prices === 'access_changed'
                  ? 'Subscription access has changed — please update your filter'
                  : 'Please set your filter' }}
              </NTooltip>
              <button class="filter-icon-btn" :class="{ active: openPanel === 'prices' }" @click="togglePanel('prices')" title="Filters">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              </button>
            </div>
          </div>
          <div class="card-content">
            <template v-if="!SUBSCRIPTION_INFO?.hasDashboards">
              <div class="skeleton-chart">
                <div class="skeleton-bars">
                  <div v-for="i in 20" :key="i" class="skeleton-bar" :style="{ height: barHeights[i-1] }" />
                </div>
              </div>
              <div class="locked-overlay">
                <i class="fa fa-lock"></i>
              </div>
            </template>
            <template v-else>
              <PriceData :embedded="true" :embedded-filters="appliedFilters.prices" @products-loaded="(p) => pricesProductOpts = p" />
              <div class="filter-panel" :class="{ open: openPanel === 'prices' }">
                <div class="filter-panel__inner">
                  <div class="filter-row">
                    <div class="filter-field">
                      <span class="filter-label">Commodity</span>
                      <NSelect v-model:value="filters.prices.element" :options="elementOptions" :theme-overrides="inputTheme" filterable clearable placeholder="Select commodity" :input-props="{ autocomplete: 'off' }" size="small" />
                    </div>
                    <div class="filter-field">
                      <span class="filter-label">Left axis</span>
                      <NSelect v-model:value="filters.prices.leftAxis" :options="pricesProductOpts.filter(o => o.value !== filters.prices.rightAxis)" :theme-overrides="inputTheme" clearable placeholder="Left axis" :disabled="!pricesProductOpts.length" :input-props="{ autocomplete: 'off' }" size="small" />
                    </div>
                    <div class="filter-field">
                      <span class="filter-label">Right axis</span>
                      <NSelect v-model:value="filters.prices.rightAxis" :options="pricesProductOpts.filter(o => o.value !== filters.prices.leftAxis)" :theme-overrides="inputTheme" clearable placeholder="Right axis" :disabled="!pricesProductOpts.length" :input-props="{ autocomplete: 'off' }" size="small" />
                    </div>
                    <NButton size="small" :disabled="!pricesFilterChanged" @click="applyFilters('prices')" color="#213ce5" style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent; align-self: flex-end;" @mouseenter="(e) => e.currentTarget.style.border = '1px solid #00aeef'" @mouseleave="(e) => e.currentTarget.style.border = '1px solid transparent'">Set</NButton>
                  </div>
                </div>
              </div>
              <div v-if="!hasAppliedPrices" class="filter-prompt">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                <p>Select your filters and click <strong>Set</strong> to load the chart</p>
              </div>
            </template>
          </div>
        </div>

        <!-- Supply Card -->
        <div class="dashboard-card cost-analysis-card">
          <div class="card-header">
            <h3 class="card-title">{{ t("App.sidebarMenu.supply") }}</h3>
            <div style="display: flex; align-items: center; gap: 6px;">
              <NTooltip v-if="attention.supply" trigger="hover" placement="top">
                <template #trigger>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#f0a020" stroke-width="2"
                       style="width: 26px; height: 26px; flex-shrink: 0; cursor: default; outline: none;">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <circle cx="12" cy="17" r="0.5" fill="#f0a020"/>
                  </svg>
                </template>
                {{ attentionReason.supply === 'access_changed'
                  ? 'Subscription access has changed — please update your filter'
                  : 'Please set your filter' }}
              </NTooltip>
              <button class="filter-icon-btn" :class="{ active: openPanel === 'supply' }" @click="togglePanel('supply')" title="Filters">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              </button>
            </div>
          </div>
          <div class="card-content">
            <template v-if="!SUBSCRIPTION_INFO?.hasDashboards">
              <div class="skeleton-chart">
                <div class="skeleton-bars">
                  <div v-for="i in 20" :key="i" class="skeleton-bar" :style="{ height: barHeights[i-1] }" />
                </div>
              </div>
              <div class="locked-overlay">
                <i class="fa fa-lock"></i>
              </div>
            </template>
            <template v-else>
              <SupplyData :embedded="true" :embedded-filters="appliedFilters.supply" @products-loaded="(p) => supplyProductOpts = p" />
              <div class="filter-panel" :class="{ open: openPanel === 'supply' }">
                <div class="filter-panel__inner">
                  <div class="filter-row">
                    <div class="filter-field">
                      <span class="filter-label">Commodity</span>
                      <NSelect v-model:value="filters.supply.element" :options="elementOptions" :theme-overrides="inputTheme" filterable clearable placeholder="Select commodity" :input-props="{ autocomplete: 'off' }" size="small" />
                    </div>
                    <div class="filter-field">
                      <span class="filter-label">Product</span>
                      <NSelect v-model:value="filters.supply.product" :options="supplyProductOpts" :theme-overrides="inputTheme" clearable placeholder="Select product" :disabled="!supplyProductOpts.length" :input-props="{ autocomplete: 'off' }" size="small" />
                    </div>
                    <NButton size="small" :disabled="!supplyFilterChanged" @click="applyFilters('supply')" color="#213ce5" style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent; align-self: flex-end;" @mouseenter="(e) => e.currentTarget.style.border = '1px solid #00aeef'" @mouseleave="(e) => e.currentTarget.style.border = '1px solid transparent'">Set</NButton>
                  </div>
                </div>
              </div>
              <div v-if="!hasAppliedSupply" class="filter-prompt">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                <p>Select your filters and click <strong>Set</strong> to load the chart</p>
              </div>
            </template>
          </div>
        </div>

      </div>

      <!-- Middle Column -->
      <div class="middle-column">

        <!-- Asset Profiles Card -->
        <div class="dashboard-card asset-profiles-card">
          <div class="card-header">
            <h3 class="card-title">{{ t("App.sidebarMenu.proximaPage") }}</h3>
            <div style="display: flex; align-items: center; gap: 6px;">
              <NTooltip v-if="attention.assets" trigger="hover" placement="top">
                <template #trigger>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#f0a020" stroke-width="2"
                       style="width: 26px; height: 26px; flex-shrink: 0; cursor: default; outline: none;">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <circle cx="12" cy="17" r="0.5" fill="#f0a020"/>
                  </svg>
                </template>
                {{ attentionReason.assets === 'access_changed'
                  ? 'Subscription access has changed — please update your filter'
                  : 'Please set your filter' }}
              </NTooltip>
              <button class="filter-icon-btn" :class="{ active: openPanel === 'assets' }" @click="togglePanel('assets')" title="Filters">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              </button>
            </div>
          </div>
          <div class="card-content map-container">
            <ProximaProfiles :embedded="true" :embedded-filters="appliedFilters.assets" />
            <div v-if="!SUBSCRIPTION_INFO?.hasAssets" class="locked-overlay">
              <i class="fa fa-lock"></i>
            </div>
            <div class="filter-panel" :class="{ open: openPanel === 'assets' }">
              <div class="filter-panel__inner">
                <div class="filter-row">
                  <div class="filter-field">
                    <span class="filter-label">Commodity</span>
                    <NSelect v-model:value="filters.assets.element" :options="assetsElementOptions" :theme-overrides="inputTheme" filterable clearable placeholder="Select commodity" :input-props="{ autocomplete: 'off' }" size="small" />
                  </div>
                  <NButton size="small" :disabled="!assetsFilterChanged" @click="applyFilters('assets')" color="#213ce5" style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent; align-self: flex-end;" @mouseenter="(e) => e.currentTarget.style.border = '1px solid #00aeef'" @mouseleave="(e) => e.currentTarget.style.border = '1px solid transparent'">Set</NButton>
                </div>
              </div>
            </div>
            <div v-if="!hasAppliedAssets" class="filter-prompt">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              <p>Select your filters and click <strong>Set</strong> to load the map</p>
            </div>
          </div>
        </div>

        <!-- Trade Card -->
        <div class="dashboard-card data-dashboards-card">
          <div class="card-header">
            <h3 class="card-title">{{ t("App.sidebarMenu.trade") }}</h3>
            <div style="display: flex; align-items: center; gap: 6px;">
              <NTooltip v-if="attention.trade" trigger="hover" placement="top">
                <template #trigger>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#f0a020" stroke-width="2"
                       style="width: 26px; height: 26px; flex-shrink: 0; cursor: default; outline: none;">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <circle cx="12" cy="17" r="0.5" fill="#f0a020"/>
                  </svg>
                </template>
                {{ attentionReason.trade === 'access_changed'
                  ? 'Subscription access has changed — please update your filter'
                  : 'Please set your filter' }}
              </NTooltip>
              <button class="filter-icon-btn" :class="{ active: openPanel === 'trade' }" @click="togglePanel('trade')" title="Filters">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              </button>
            </div>
          </div>
          <div class="card-content">
            <template v-if="!SUBSCRIPTION_INFO?.hasDashboards">
              <div class="skeleton-chart">
                <div class="skeleton-bars">
                  <div v-for="i in 20" :key="i" class="skeleton-bar" :style="{ height: barHeights[i-1] }" />
                </div>
              </div>
              <div class="locked-overlay">
                <i class="fa fa-lock"></i>
              </div>
            </template>
            <TradeData v-else :embedded="true" :embedded-filters="appliedFilters.trade"
                       @products-loaded="(p) => tradeProductOpts = p" />
            <div v-if="!hasAppliedTrade" class="filter-prompt">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              <p>Select your filters and click <strong>Set</strong> to load the chart</p>
            </div>
            <div class="filter-panel" :class="{ open: openPanel === 'trade' }">
              <div class="filter-panel__inner">
                <div class="filter-row">
                  <div class="filter-field">
                    <span class="filter-label">Commodity</span>
                    <NSelect v-model:value="filters.trade.element" :options="elementOptions" :theme-overrides="inputTheme" filterable clearable placeholder="Select commodity" :input-props="{ autocomplete: 'off' }" size="small" />
                  </div>
                  <div class="filter-field">
                    <span class="filter-label">Product</span>
                    <NSelect v-model:value="filters.trade.product" :options="tradeProductOpts"
                             :theme-overrides="inputTheme" clearable placeholder="Select product"
                             :disabled="!tradeProductOpts.length" :input-props="{ autocomplete: 'off' }" size="small" />
                  </div>
                  <NButton size="small" :disabled="!tradeFilterChanged" @click="applyFilters('trade')" color="#213ce5" style="background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent; align-self: flex-end;" @mouseenter="(e) => e.currentTarget.style.border = '1px solid #00aeef'" @mouseleave="(e) => e.currentTarget.style.border = '1px solid transparent'">Set</NButton>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Right Column -->
      <div class="right-column">
        <div class="dashboard-card news-feed-card">
          <div class="card-header">
            <h3 class="card-title">Latest News</h3>
          </div>
          <div class="card-content news-content">
            <LatestNews />
          </div>
        </div>
      </div>

    </div>
    <!-- Subscription Selection Modal -->
    <NModal v-model:show="showSubscriptionModal" :mask-closable="false">
      <NCard
          style="width: 480px; max-width: 95vw;"
          title="Select Dashboard Subscription"
          :bordered="false"
      >
        <p style="font-size: 13px; color: #606266; margin-bottom: 16px;">
          You have access to multiple subscriptions. Select which subscription's
          default filters to use for your dashboard.
        </p>
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <NButton
              v-for="sub in activeSubscriptions"
              :key="sub.id"
              :theme-overrides="{
            colorHover: 'linear-gradient(90deg, #213ce5, #2380f2)',
            colorPressed: '#213ce5',
            colorFocus: '#213ce5',
            borderHover: '1px solid #00aeef',
            borderPressed: '1px solid #00aeef',
            borderFocus: '1px solid #00aeef',
            textColorHover: '#fff',
            textColorPressed: '#fff',
            textColorFocus: '#fff',
          }"
              color="#213ce5"
              style="justify-content: flex-start; background: linear-gradient(90deg, #213ce5, #2380f2); border: 1px solid transparent;"
              @click="onSubscriptionModalSelect(sub.id)"
          >
            {{ sub.packageName }}
          </NButton>
        </div>
        <template #footer>
          <NButton
              :theme-overrides="{
            borderHover: '1px solid #00aeef',
            borderPressed: '1px solid #00aeef',
            borderFocus: '1px solid #00aeef',
            textColorHover: '#213ce5',
            textColorPressed: '#213ce5',
            textColorFocus: '#213ce5',
          }"
              style="border: 1px solid #213ce5; color: #213ce5;"
              @click="onSubscriptionModalDismiss"
          >
            Skip — use empty filters
          </NButton>
        </template>
      </NCard>
    </NModal>
  </div>
</template>

<script setup>
import { onBeforeMount, onMounted, ref, computed, watch, nextTick } from "vue";
import { NSelect, NButton, NModal, NCard, NTooltip } from "naive-ui";
import {
  SUBSCRIPTION_INFO,
  SUBSCRIPTION_ELEMENTS_OPTS,
  getSubscriptionInfo,
  getDashboardPreferences,
  saveDashboardPreferences,
  dashboardPreferences,
} from "../../composables/subscription";
import { getMapElements } from "../../composables/maps";
import PriceData from "../MarketData/PriceData.vue";
import LatestNews from "../../components/HomePage/LatestNews.vue";
import SupplyData from "../MarketData/SupplyData.vue";
import MarketDataDashboard from "../MarketData/MarketDataDashboard.vue";
import ProximaProfiles from "../../pages/ProximaProfiles/List.vue";
import { useI18n } from "vue-i18n";
import TradeData from "../MarketData/TradeData.vue";
const { t } = useI18n();
const hasAppliedPrices  = computed(() => !!appliedFilters.value.prices?.element && (!!appliedFilters.value.prices?.leftAxis || !!appliedFilters.value.prices?.rightAxis));
const hasAppliedAssets = computed(() => !!appliedFilters.value.assets?.element);
const hasAppliedSupply  = computed(() => !!appliedFilters.value.supply?.product);
const hasAppliedTrade   = computed(() => !!appliedFilters.value.trade?.product);
const isSubscriptionLoaded = ref(false);
const barHeights = ['45%','62%','38%','71%','55%','80%','48%','65%','42%','73%','58%','68%','48%','65%','42%','73%','58%','68%','73%','58%'];
// ── Dashboard preferences ─────────────────────────────────────────────────────
const preferencesLoaded         = ref(false);
const attention                 = ref({ prices: false, supply: false, assets: false, trade: false });
const preferencesSource         = ref(null);
const showSubscriptionModal     = ref(false);
const selectedModalSubscription = ref(null);
const seedingPreferences = ref(false);
const attentionReason = ref({ prices: null, supply: null, assets: null, trade: null });
// null | "access_changed" | "no_filter"
// ── Naive UI theme — matches PriceData.vue exactly ────────────────────────────
const inputTheme = {
  peers: {
    InternalSelection: {
      borderHover: '1px solid #213ce5', borderFocus: '1px solid #213ce5',
      borderActive: '1px solid #213ce5', boxShadowFocus: '0 0 0 2px rgba(33, 60, 229, 0.2)',
      boxShadowActive: '0 0 0 2px rgba(33, 60, 229, 0.2)', boxShadowHover: 'none',
      caretColor: '#213ce5', arrowColorActive: '#213ce5', arrowColorHover: '#213ce5',
      color: '#fff', colorActive: '#ffffff', colorFocus: '#ffffff',
      fontFamily: "'GilroyMedium', sans-serif", fontSize: '11px !important',
    },
    InternalSelectMenu: {
      optionColorActive: 'rgba(33, 60, 229, 0.08)', optionColorActivePending: 'rgba(33, 60, 229, 0.12)',
      optionColorPending: 'rgba(33, 60, 229, 0.05)', optionTextColorActive: '#213ce5',
      optionTextColorChildActive: '#213ce5', optionCheckColor: '#213ce5',
      actionDividerColor: '#213ce5', loadingColor: '#213ce5',
      scrollbarColor: 'rgba(33, 60, 229, 0.3)', scrollbarColorHover: 'rgba(33, 60, 229, 0.5)',
      fontFamily: "'GilroyMedium', sans-serif", fontSize: '11px !important',
    },
  },
};

// ── Filter panel state ─────────────────────────────────────────────────────────
const openPanel = ref(null);
const togglePanel = (key) => { openPanel.value = openPanel.value === key ? null : key; };

// Product/axis option lists — populated via @products-loaded emit from child components
// Shape: [{ label, value }] — same format NSelect expects
const pricesProductOpts = ref([]);
const supplyProductOpts  = ref([]);
const tradeProductOpts = ref([]);
const activeSubscriptions = computed(() =>
    (SUBSCRIPTION_INFO?.parentSubscriptionPackages || []).filter(
        p => p.id && p.packageName
    )
);

const hasMultipleSubscriptions = computed(() =>
    activeSubscriptions.value.length > 1
);
// Element options — matches ELEMENT_OPTS in PriceData.vue exactly (SUBSCRIPTION_INFO is reactive, not a ref)
const elementOptions = computed(() =>
    SUBSCRIPTION_INFO?.dataElements?.map(e => ({ label: e.name, value: e.code })) ?? []
);
// Asset profiles card matches the full ProximaProfiles view — commodities from assets access, not dashboards access
const assetsElementOptions = computed(() => SUBSCRIPTION_ELEMENTS_OPTS.value ?? []);

// ── Staging filters (two-phase: edit → Set → apply) ───────────────────────────
const filters = ref({
  prices: { element: null, leftAxis: null, rightAxis: null },
  supply: { element: null, product: null },
  assets: { element: null },
  trade: { element: null, product: null },
});
const tradeFilterChanged = computed(() => {
  const a = appliedFilters.value.trade;
  const f = filters.value.trade;
  if (!f.element || !f.product) return false;
  return !a || f.element !== a.element || f.product !== a.product;
});

const supplyFilterChanged = computed(() => {
  const a = appliedFilters.value.supply;
  const f = filters.value.supply;
  if (!f.element || !f.product) return false;
  return !a || f.element !== a.element || f.product !== a.product;
});

const pricesFilterChanged = computed(() => {
  const a = appliedFilters.value.prices;
  const f = filters.value.prices;
  if (!f.element) return false;
  return !a || f.element !== a.element || f.leftAxis !== a.leftAxis || f.rightAxis !== a.rightAxis;
});

const assetsFilterChanged = computed(() => {
  const a = appliedFilters.value.assets;
  const f = filters.value.assets;
  return !a || f.element !== a.element;
});
// ── Applied filters — only updated on Set, passed to child as :embedded-filters ─
const appliedFilters = ref({
  prices: null,
  supply: null,
  assets: null,
  trade:  null,
});

function seedFiltersFromPreferences(prefs) {
  const p = prefs?.preferences;
  const a = prefs?.attention ?? { prices: false, supply: false, assets: false, trade: false };

  seedingPreferences.value = true;

  // Prices
  if (p?.prices?.element) {
    filters.value.prices.element   = p.prices.element;
    filters.value.prices.leftAxis  = p.prices.leftAxis  ?? null;
    filters.value.prices.rightAxis = p.prices.rightAxis ?? null;
    appliedFilters.value.prices    = {
      element:   p.prices.element,
      leftAxis:  p.prices.leftAxis  ?? null,
      rightAxis: p.prices.rightAxis ?? null,
    };
    attention.value.prices       = false;
    attentionReason.value.prices = null;
  } else {
    appliedFilters.value.prices  = null;
    filters.value.prices         = { element: null, leftAxis: null, rightAxis: null };
    attention.value.prices       = true;
    attentionReason.value.prices = a.prices ? 'access_changed' : 'no_filter';
  }

  // Supply
  if (p?.supply?.element) {
    filters.value.supply.element = p.supply.element;
    filters.value.supply.product = p.supply.product ?? null;
    appliedFilters.value.supply  = {
      element: p.supply.element,
      product: p.supply.product ?? null,
    };
    attention.value.supply       = false;
    attentionReason.value.supply = null;
  } else {
    appliedFilters.value.supply  = null;
    filters.value.supply         = { element: null, product: null };
    attention.value.supply       = true;
    attentionReason.value.supply = a.supply ? 'access_changed' : 'no_filter';
  }

  // Assets
  if (p?.assets?.element) {
    filters.value.assets.element = p.assets.element;
    appliedFilters.value.assets  = { element: p.assets.element };
    attention.value.assets       = false;
    attentionReason.value.assets = null;
  } else {
    appliedFilters.value.assets  = null;
    filters.value.assets         = { element: null };
    attention.value.assets       = true;
    attentionReason.value.assets = a.assets ? 'access_changed' : 'no_filter';
  }

  // Trade
  if (p?.trade?.element) {
    filters.value.trade.element = p.trade.element;
    filters.value.trade.product = p.trade.product ?? null;
    appliedFilters.value.trade  = {
      element: p.trade.element,
      product: p.trade.product ?? null,
    };
    attention.value.trade       = false;
    attentionReason.value.trade = null;
  } else {
    appliedFilters.value.trade  = null;
    filters.value.trade         = { element: null, product: null };
    attention.value.trade       = true;
    attentionReason.value.trade = a.trade ? 'access_changed' : 'no_filter';
  }

  nextTick(() => {
    seedingPreferences.value = false;
  });
}

async function onSubscriptionModalSelect(subscriptionId) {
  showSubscriptionModal.value     = false;
  selectedModalSubscription.value = subscriptionId;
  await getDashboardPreferences(subscriptionId);
  seedFiltersFromPreferences(dashboardPreferences);
  preferencesLoaded.value = true;
}

function onSubscriptionModalDismiss() {
  showSubscriptionModal.value = false;
  preferencesLoaded.value     = true;
}

function applyFilters(key) {
  if (key === 'prices' && filters.value.prices.leftAxis == null && filters.value.prices.rightAxis != null) {
    // Normalize a single axis selection onto the left axis so the filter UI matches the chart
    filters.value.prices.leftAxis  = filters.value.prices.rightAxis;
    filters.value.prices.rightAxis = null;
  }

  appliedFilters.value[key] = JSON.parse(JSON.stringify(filters.value[key]));
  openPanel.value = null;
  attention.value[key]       = false;
  attentionReason.value[key] = null;

  const subId = selectedModalSubscription.value ?? activeSubscriptions.value[0]?.id;
  if (subId) {
    saveDashboardPreferences(parseInt(subId), {
      prices: appliedFilters.value.prices?.element ? appliedFilters.value.prices : null,
      supply: appliedFilters.value.supply?.element ? appliedFilters.value.supply : null,
      assets: appliedFilters.value.assets?.element ? appliedFilters.value.assets : null,
      trade:  appliedFilters.value.trade?.element  ? appliedFilters.value.trade  : null,
    });
  }
}

// ── Seed staging selects once subscription data is ready ──────────────────────
function initFilters() {
  filters.value.prices  = { element: null, leftAxis: null, rightAxis: null };
  filters.value.supply  = { element: null, product: null };
  filters.value.assets  = { element: null };
  filters.value.trade   = { element: null, product: null };

  // seed applied so buttons start disabled
  appliedFilters.value.prices  = null;
  appliedFilters.value.supply  = null;
  appliedFilters.value.assets  = null;
  appliedFilters.value.trade   = null;
}
watch(
    () => filters.value.prices.element,
    async (newElement) => {
      if (seedingPreferences.value) return; // ← add this
      pricesProductOpts.value = [];
      filters.value.prices.leftAxis = null;
      filters.value.prices.rightAxis = null;
      if (!newElement) return;
      if (newElement !== appliedFilters.value.prices?.element) {
        appliedFilters.value.prices = { element: newElement, leftAxis: null, rightAxis: null };
      }
    }
);

watch(
    () => filters.value.supply.element,
    (newElement) => {
      if (seedingPreferences.value) return; // ← add this
      supplyProductOpts.value = [];
      filters.value.supply.product = null;
      if (!newElement) return;
      if (newElement !== appliedFilters.value.supply?.element) {
        appliedFilters.value.supply = { element: newElement, product: null };
      }
    }
);

watch(
    () => filters.value.trade.element,
    (newElement) => {
      if (seedingPreferences.value) return; // ← add this
      tradeProductOpts.value = [];
      filters.value.trade.product = null;
      if (!newElement) return;
      if (newElement !== appliedFilters.value.trade?.element) {
        appliedFilters.value.trade = { element: newElement, product: null };
      }
    }
);
// In filters.prices watch or as a computed guard
watch(() => filters.value.prices.leftAxis, (newVal) => {
  if (newVal && newVal === filters.value.prices.rightAxis) {
    filters.value.prices.rightAxis = null;
  }
});

watch(() => filters.value.prices.rightAxis, (newVal) => {
  if (newVal && newVal === filters.value.prices.leftAxis) {
    filters.value.prices.leftAxis = null;
  }
});
// ── Load preferences once subscription data is ready ──────────────────────────
const preferencesInitialised = ref(false);

watch(
    () => SUBSCRIPTION_INFO?.parentSubscriptionPackages?.length,
    async (length) => {
      if (!length || preferencesInitialised.value) return;
      preferencesInitialised.value = true;

      const subs = activeSubscriptions.value;
      console.log('subs loaded:', subs);

      if (subs.length === 0) {
        preferencesLoaded.value = true;

      } else if (subs.length === 1) {
        selectedModalSubscription.value = subs[0].id;
        await getDashboardPreferences(subs[0].id);
        seedFiltersFromPreferences(dashboardPreferences);
        preferencesLoaded.value = true;

      } else {
        let loaded = false;
        for (const sub of subs) {
          const response = await getDashboardPreferences(sub.id);
          if (response?.source === 'user') {
            selectedModalSubscription.value = sub.id;
            seedFiltersFromPreferences(dashboardPreferences);
            preferencesLoaded.value = true;
            loaded = true;
            break;
          }
        }
        if (!loaded) {
          showSubscriptionModal.value = true;
        }
      }
    },
    { immediate: true }
);
// ── Lifecycle ──────────────────────────────────────────────────────────────────
onBeforeMount(async () => { window.$loading.start(); });

onMounted(async () => {
  try {
    if (!SUBSCRIPTION_INFO || !SUBSCRIPTION_INFO.elements) {
      await getSubscriptionInfo(true);
    }
    isSubscriptionLoaded.value = true;
    initFilters();
    await getMapElements();
  } catch (error) {
    // silent
  } finally {
    if (SUBSCRIPTION_INFO) window.$loading.finish();
  }
});
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────────────── */
.dashboard-container {
  height: calc(100vh - 70px);
  width: 100%;
  overflow: hidden;
  padding: 0.5rem;
}

:deep(.content-wrapper) { padding: 0 !important; }

.dashboard-grid {
  display: grid;
  grid-template-columns: 1.25fr 1.25fr 1fr;
  gap: 0.5rem;
  width: 100%;
  height: 100%;
}

.left-column,
.middle-column,
.right-column {
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
}

/* ── Cards ──────────────────────────────────────────────────────────────────── */
.dashboard-card {
  background: #f8f9fa;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
}

.news-feed-card { height: 100%; flex: 1; }

/* ── Card header ─────────────────────────────────────────────────────────────── */
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: #f8f9fa;
  flex-shrink: 0;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  margin: 0;
}

/* ── Filter icon button ──────────────────────────────────────────────────────── */
.filter-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: none;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  cursor: pointer;
  color: #555;
  padding: 4px;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.filter-icon-btn svg { width: 14px; height: 14px; }
.filter-icon-btn:hover,
.filter-icon-btn.active {
  background: #213ce5;
  border-color: #213ce5;
  color: #fff;
}

/* ── Filter overlay ──────────────────────────────────────────────────────────── */
.filter-panel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.22s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.filter-panel.open { max-height: 90px; }

.filter-panel__inner {
  background: #fff;
  border-bottom: 2px solid #213ce5;
  padding: 0.5rem 0.6rem;
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 0;
  min-width: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
}

/* NSelect inside filter panel — override background so it reads on white */
.filter-panel :deep(.n-base-selection) {
  background-color: #fff !important;
}
.filter-prompt {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #f8f9fa;
  z-index: 5;
  color: #000;
  text-align: center;
  padding: 0.5rem;
}

.filter-prompt svg {
  width: 20px;
  height: 20px;
  opacity: 0.4;
}

.filter-prompt p {
  font-size: 0.75rem !important;
  color: #000;
  margin: 0;
}

.filter-prompt strong {
  color: #213ce5;
}
/* ── Card content ────────────────────────────────────────────────────────────── */
.card-content {
  position: relative;
  flex: 1;
  background: #f8f9fa;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.card-content.news-content { padding: 0; }
.card-content.map-container { padding: 0; }

/* ── Locked overlay ──────────────────────────────────────────────────────────── */
.locked-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 33, 59, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.locked-overlay i { font-size: 2rem; color: #fff; }

/* ── Skeleton ────────────────────────────────────────────────────────────────── */
.skeleton-chart {
  position: absolute;
  inset: 0;
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 0.5rem;
}
.skeleton-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 80%;
  gap: 6px;
}
.skeleton-bar {
  flex: 1;
  background: #e0e0e0;
  border-radius: 2px 2px 0 0;
}

/* ── Responsive ──────────────────────────────────────────────────────────────── */
@media (max-width: 1199.98px) {
  .dashboard-container {
    height: auto;
    min-height: calc(100vh - 70px);
    overflow: auto;
  }
  .dashboard-grid {
    grid-template-columns: repeat(2, 1fr);
    height: auto;
    min-height: calc(100vh - 140px);
  }
  .left-column   { grid-column: 1; grid-row: 1; }
  .middle-column { grid-column: 2; grid-row: 1; }
  .right-column  { grid-column: 1 / 3; grid-row: 2; height: 400px; flex-direction: row; }
}

@media (max-width: 767.98px) {
  .dashboard-container {
    height: auto;
    min-height: calc(100vh - 60px);
    overflow: auto;
    padding: 0.5rem 0.5rem 1rem;
  }

  .dashboard-grid {
    display: flex;
    flex-direction: column;
    grid-template-columns: none;
    height: auto;
  }

  .left-column,
  .middle-column,
  .right-column {
    height: auto;
  }

  .right-column {
    flex-direction: column;
  }

  .dashboard-card {
    flex: none;
    height: 400px;
  }

  .news-feed-card {
    height: 400px;
  }

  .filter-row {
    flex-wrap: wrap;
  }

  .filter-field {
    flex: 1 1 45%;
  }
}
</style>